/**
 * Super Admin — Ticket Analytics.
 *
 * Wires to /api/superadmin/support/analytics + /queue. Renders:
 *   · Totals strip (open / resolved / escalated / breach rate)
 *   · Avg + p90 time-to-resolution
 *   · SLA-breach rate per category (bar list)
 *   · Top escalation keywords (word bubbles)
 *   · 14-day inflow trend (mini sparkline)
 *   · Escalated tickets table
 */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import {
  LifeBuoy, Clock, ShieldAlert, Zap, TrendingUp, Flame, Tag, AlertTriangle,
} from "lucide-react";
import { Link } from "react-router-dom";

interface Analytics {
  totals: {
    total: number; open: number; in_progress: number; resolved: number;
    escalated: number; sla_breached: number;
    resolution_rate_pct: number; breach_rate_pct: number;
  };
  avg_time_to_resolution_hours: number;
  p90_time_to_resolution_hours: number;
  by_category: Array<{ category: string; total: number; breached: number; resolved: number; breach_rate_pct: number }>;
  by_severity: { low: number; medium: number; high: number };
  top_escalation_keywords: Array<{ word: string; count: number }>;
  inflow_trend_14d: Array<{ day: string; count: number }>;
}

interface QueueItem {
  ticket_id: string; subject: string; category: string; severity: string;
  status: string; escalated: boolean; sla_deadline?: string;
  sla_breached_at?: string; created_at: string;
}

export default function SuperAdminSupport() {
  const analytics = useQuery<Analytics>({
    queryKey: ["sa-support-analytics"],
    queryFn: async () => (await api.get("/superadmin/support/analytics")).data,
    refetchInterval: 30_000,
  });
  const queue = useQuery<{ items: QueueItem[] }>({
    queryKey: ["sa-support-queue"],
    queryFn: async () => (await api.get("/superadmin/support/queue?limit=25")).data,
    refetchInterval: 30_000,
  });

  const a = analytics.data;
  const totals = a?.totals;

  return (
    <DashboardLayout
      title="Ticket Analytics"
      subtitle="Live health of your support pipeline — time-to-resolution, SLA breaches, and where users get stuck."
      seoNoindex
    >
      {/* Header strip */}
      <div className="rounded-2xl overflow-hidden relative border border-[color:var(--sg-border)] mb-6" data-testid="sa-support-header">
        <div className="absolute inset-0 bg-gradient-to-br from-[color:var(--sb-navy,#0A1128)] to-rose-900" />
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 20% 40%, rgba(249,115,22,0.35), transparent 40%), radial-gradient(circle at 80% 60%, rgba(244,63,94,0.3), transparent 40%)" }}/>
        <div className="relative px-6 py-6 sm:px-8 sm:py-7 text-white flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-white/70 flex items-center gap-1.5">
              <LifeBuoy className="h-3 w-3" strokeWidth={2.5}/> Support Ops · live
            </div>
            <div className="mt-2 font-serif-display text-3xl sm:text-4xl font-bold leading-tight">
              {totals?.total ?? 0} tickets · {totals?.resolution_rate_pct ?? 0}% resolved · {totals?.breach_rate_pct ?? 0}% SLA-breached
            </div>
            <div className="mt-2 text-sm text-white/70 max-w-2xl">
              Time-to-resolution and category-level breach rates so you know where to invest the next hire.
            </div>
          </div>
          <Link
            to="/workspace/support/help"
            data-testid="sa-support-open-helpcenter"
            className="inline-flex items-center h-10 px-4 rounded-full bg-white/95 text-[color:var(--sb-navy,#0A1128)] font-semibold text-sm hover:bg-white"
          >
            Open Help Center →
          </Link>
        </div>
      </div>

      {/* Totals row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi label="Open · Live" value={String((totals?.open ?? 0) + (totals?.in_progress ?? 0))} hint={`${totals?.escalated ?? 0} escalated`} icon={LifeBuoy} tone="blue" testid="sa-kpi-open"/>
        <Kpi label="Resolved · All-time" value={String(totals?.resolved ?? 0)} hint={`${totals?.resolution_rate_pct ?? 0}% rate`} icon={TrendingUp} tone="emerald" testid="sa-kpi-resolved"/>
        <Kpi label="Avg time-to-resolution" value={`${a?.avg_time_to_resolution_hours ?? 0}h`} hint={`p90 · ${a?.p90_time_to_resolution_hours ?? 0}h`} icon={Clock} tone="fuchsia" testid="sa-kpi-avgttr"/>
        <Kpi label="SLA breaches" value={String(totals?.sla_breached ?? 0)} hint={`${totals?.breach_rate_pct ?? 0}% of all tickets`} icon={ShieldAlert} tone="rose" testid="sa-kpi-breach"/>
      </div>

      {/* Two-column: category breach rate + severity split */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-4 mb-6">
        <CategoryPanel rows={a?.by_category || []}/>
        <SeverityPanel by={a?.by_severity || { low: 0, medium: 0, high: 0 }}/>
      </div>

      {/* Keywords + trend */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-4 mb-6">
        <KeywordsPanel keywords={a?.top_escalation_keywords || []}/>
        <TrendPanel trend={a?.inflow_trend_14d || []}/>
      </div>

      {/* Escalated tickets table */}
      <EscalatedTable items={queue.data?.items || []}/>
    </DashboardLayout>
  );
}

function Kpi(p: { label: string; value: string; hint: string; icon: any; tone: string; testid: string }) {
  const Icon = p.icon;
  const toneClasses: Record<string, string> = {
    blue: "text-blue-600 dark:text-blue-300 from-blue-500/15",
    emerald: "text-emerald-600 dark:text-emerald-300 from-emerald-500/15",
    fuchsia: "text-fuchsia-600 dark:text-fuchsia-300 from-fuchsia-500/15",
    rose: "text-rose-600 dark:text-rose-300 from-rose-500/15",
  };
  return (
    <div data-testid={p.testid} className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-4 relative overflow-hidden">
      <div className={`absolute -top-6 -right-6 h-24 w-24 rounded-full bg-gradient-to-br ${toneClasses[p.tone]} to-transparent blur-2xl`}/>
      <div className="relative">
        <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold ${toneClasses[p.tone].split(" ")[0]} ${toneClasses[p.tone].split(" ")[1]}`}>
          <Icon className="h-3 w-3" strokeWidth={2.5}/> {p.label}
        </div>
        <div className="mt-2 font-serif-display text-3xl font-bold text-[color:var(--sg-fg)] tabular-nums">{p.value}</div>
        <div className="text-[11px] text-[color:var(--sg-muted)] mt-0.5">{p.hint}</div>
      </div>
    </div>
  );
}

function CategoryPanel({ rows }: { rows: Analytics["by_category"] }) {
  const maxBreach = Math.max(1, ...rows.map((r) => r.breach_rate_pct));
  return (
    <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-5" data-testid="sa-cat-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sg-muted)] flex items-center gap-1.5">
          <Tag className="h-3 w-3"/> SLA breach rate · by category
        </div>
        <span className="text-[10px] text-[color:var(--sg-muted)]">{rows.length} categories</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-[color:var(--sg-muted)] py-4">No category data yet.</div>
      ) : (
        <div className="space-y-2.5">
          {rows.map((r) => {
            const pct = (r.breach_rate_pct / maxBreach) * 100;
            const tone = r.breach_rate_pct >= 40 ? "rose" : r.breach_rate_pct >= 20 ? "amber" : "emerald";
            return (
              <div key={r.category} data-testid={`sa-cat-${r.category}`}>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-[color:var(--sg-fg)]">{r.category}</span>
                  <span className="text-[color:var(--sg-muted)] tabular-nums">
                    {r.breached}/{r.total} breached · <b className={`text-${tone}-700 dark:text-${tone}-300`}>{r.breach_rate_pct}%</b>
                  </span>
                </div>
                <div className="mt-1 h-2 rounded-full bg-[color:var(--sg-muted-bg)] overflow-hidden">
                  <div className={`h-full rounded-full bg-${tone}-500`}
                       style={{ width: `${Math.max(3, pct)}%`, transition: "width 500ms" }}/>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SeverityPanel({ by }: { by: Analytics["by_severity"] }) {
  const rows = [
    { label: "High",   count: by.high,   tone: "rose",    hint: "2h SLA" },
    { label: "Medium", count: by.medium, tone: "amber",   hint: "6h SLA" },
    { label: "Low",    count: by.low,    tone: "emerald", hint: "24h SLA" },
  ];
  const total = Math.max(1, by.high + by.medium + by.low);
  return (
    <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-5" data-testid="sa-sev-panel">
      <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sg-muted)] mb-3 flex items-center gap-1.5">
        <Zap className="h-3 w-3"/> Severity split
      </div>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.label} data-testid={`sa-sev-${r.label}`}>
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-[color:var(--sg-fg)]">{r.label}</span>
              <span className="text-[color:var(--sg-muted)] tabular-nums">{r.count} · {r.hint}</span>
            </div>
            <div className="mt-1 h-2 rounded-full bg-[color:var(--sg-muted-bg)] overflow-hidden">
              <div className={`h-full rounded-full bg-${r.tone}-500`} style={{ width: `${(r.count / total) * 100}%`, transition: "width 500ms" }}/>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function KeywordsPanel({ keywords }: { keywords: Analytics["top_escalation_keywords"] }) {
  const max = Math.max(1, ...keywords.map((k) => k.count));
  return (
    <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-5" data-testid="sa-kw-panel">
      <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sg-muted)] mb-3 flex items-center gap-1.5">
        <Flame className="h-3 w-3"/> Top escalation keywords
      </div>
      {keywords.length === 0 ? (
        <div className="text-sm text-[color:var(--sg-muted)] py-4">Nothing escalated yet.</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {keywords.map((k) => {
            const scale = 0.85 + (k.count / max) * 0.6;
            return (
              <span
                key={k.word}
                data-testid={`sa-kw-${k.word}`}
                className="inline-flex items-center gap-1 rounded-full border border-rose-300/60 bg-rose-500/10 px-2.5 py-1 text-rose-800 dark:text-rose-200 font-semibold"
                style={{ fontSize: `${11 * scale}px` }}
              >
                {k.word} <span className="text-[10px] text-rose-600 dark:text-rose-300">·{k.count}</span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TrendPanel({ trend }: { trend: Analytics["inflow_trend_14d"] }) {
  const max = Math.max(1, ...trend.map((t) => t.count));
  return (
    <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-5" data-testid="sa-trend-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sg-muted)] flex items-center gap-1.5">
          <TrendingUp className="h-3 w-3"/> Ticket inflow · last 14 days
        </div>
        <span className="text-[10px] text-[color:var(--sg-muted)]">
          {trend.reduce((s, t) => s + t.count, 0)} total
        </span>
      </div>
      {trend.length === 0 ? (
        <div className="text-sm text-[color:var(--sg-muted)] py-4">No tickets in the last 14 days.</div>
      ) : (
        <div className="flex items-end justify-between gap-1 h-28">
          {trend.map((t) => {
            const h = (t.count / max) * 100;
            return (
              <div key={t.day} className="flex-1 flex flex-col items-center gap-1" title={`${t.day}: ${t.count}`}>
                <div className="w-full flex-1 flex items-end">
                  <div className="w-full rounded-t-md bg-gradient-to-t from-rose-500 to-[color:var(--sb-saffron,#F97316)] shadow-lg shadow-fuchsia-500/20"
                       style={{ height: `${Math.max(4, h)}%`, transition: "height 500ms ease" }}/>
                </div>
                <div className="text-[9px] font-mono text-[color:var(--sg-muted)]">{t.day.slice(5)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EscalatedTable({ items }: { items: QueueItem[] }) {
  return (
    <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-5" data-testid="sa-escalated">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sg-muted)] flex items-center gap-1.5">
          <AlertTriangle className="h-3 w-3"/> Escalated + breached tickets
        </div>
        <span className="text-[10px] text-[color:var(--sg-muted)]">{items.length} shown</span>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-[color:var(--sg-muted)] py-4">Nothing escalated right now. Nice.</div>
      ) : (
        <div className="overflow-x-auto sg-scrollbar-thin">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)]">
                <th className="text-left py-1.5">Ticket</th>
                <th className="text-left py-1.5">Category</th>
                <th className="text-left py-1.5">Severity</th>
                <th className="text-left py-1.5">Status</th>
                <th className="text-left py-1.5">Age</th>
                <th className="text-left py-1.5">Subject</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const created = new Date(it.created_at);
                const ageH = Math.max(0, Math.round((Date.now() - created.getTime()) / 3600000));
                const sevTone = it.severity === "high" ? "rose" : it.severity === "medium" ? "amber" : "emerald";
                return (
                  <tr key={it.ticket_id} className="border-t border-[color:var(--sg-border)]/60" data-testid={`sa-esc-${it.ticket_id}`}>
                    <td className="py-1.5 pr-3 font-mono text-[10px] text-[color:var(--sg-fg)]">
                      <Link to={`/workspace/support/help/${it.ticket_id}`} className="hover:underline">
                        {it.ticket_id}
                      </Link>
                    </td>
                    <td className="py-1.5 pr-3 text-[color:var(--sg-fg)]">{it.category}</td>
                    <td className="py-1.5 pr-3">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-${sevTone}-100 text-${sevTone}-800 dark:bg-${sevTone}-900/40 dark:text-${sevTone}-200`}>
                        {it.severity}
                      </span>
                    </td>
                    <td className="py-1.5 pr-3">{it.escalated ? <span className="text-rose-700 dark:text-rose-300 font-semibold">Escalated</span> : it.status}</td>
                    <td className="py-1.5 pr-3 tabular-nums">{ageH}h</td>
                    <td className="py-1.5 text-[color:var(--sg-fg)] truncate max-w-[420px]">{it.subject}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
